import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { GET_SOCIAL_AUTH_URL, HANDLE_SOCIAL_CALLBACK } from "./graphql/queries";

const SocialAuthPopup = () => {
  const location = useLocation();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Initializing...");

  const urlParams = new URLSearchParams(location.search);
  const provider = urlParams.get("provider");
  const eventLink = urlParams.get("eventLink");
  const iframeOrigin = urlParams.get("origin") || "*";

  const [getSocialAuthUrl] = useLazyQuery(GET_SOCIAL_AUTH_URL);
  const [handleSocialCallback] = useLazyQuery(HANDLE_SOCIAL_CALLBACK);

  useEffect(() => {
    if (!provider || !eventLink) {
      setError("Missing required parameters: provider or eventLink");
      return;
    }

    const initializeAuth = async () => {
      try {
        setStatus(`Connecting to ${provider}...`);

        console.log("🔍 SocialAuthPopup - Getting auth URL for:", {
          provider,
          eventLink,
        });

        // Get the OAuth URL from backend
        const result = await getSocialAuthUrl({
          variables: { provider: provider.toLowerCase(), link: eventLink },
        });

        if (result.data?.getSocialAuthUrl?.authUrl) {
          const authUrl = result.data.getSocialAuthUrl.authUrl;

          console.log(
            "🔍 SocialAuthPopup - Redirecting to OAuth provider:",
            authUrl
          );

          // Redirect to OAuth provider
          window.location.href = authUrl;
        } else {
          throw new Error("Failed to get authentication URL");
        }
      } catch (error) {
        console.error("🚨 SocialAuthPopup - Auth initialization error:", error);
        setError(error.message || "Authentication initialization failed");

        if (window.opener) {
          window.opener.postMessage(
            {
              type: "SOCIAL_AUTH_ERROR",
              provider: provider,
              error: error.message || "Authentication initialization failed",
            },
            iframeOrigin
          );
        }
      }
    };

    // Check if we're returning from OAuth (has code parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code) {
      handleAuthCallback(code, state);
    } else {
      initializeAuth();
    }
  }, [provider, eventLink, iframeOrigin]);

  const handleAuthCallback = async (code, state) => {
    try {
      setStatus("Processing authentication...");

      console.log("🔍 SocialAuthPopup - Handling callback with:", {
        code: code?.substring(0, 10) + "...",
        state,
      });

      // Handle the OAuth callback
      const result = await handleSocialCallback({
        variables: {
          provider: provider.toLowerCase(),
          code,
          state,
          link: eventLink,
        },
      });

      if (result.data?.handleSocialCallback?.success) {
        const response = result.data.handleSocialCallback;

        // Parse the response data
        const socialData = JSON.parse(response.socialData);
        const mappedData = JSON.parse(response.mappedData);
        const missingFields = JSON.parse(response.missingFields);

        setStatus("Authentication successful! Sending data...");

        if (window.opener) {
          const authData = {
            type: "SOCIAL_AUTH_SUCCESS",
            provider: provider,
            socialData,
            mappedData,
            missingFields,
          };

          console.log("🔍 SocialAuthPopup - Sending data to iframe:", authData);

          window.opener.postMessage(authData, iframeOrigin);

          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          setError("Unable to communicate with parent window");
        }
      } else {
        const errorMessage =
          result.data?.handleSocialCallback?.error || "Authentication failed";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("🚨 SocialAuthPopup - Callback handling error:", error);
      setError(error.message || "Authentication processing failed");

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "SOCIAL_AUTH_ERROR",
            provider: provider,
            error: error.message || "Authentication processing failed",
          },
          iframeOrigin
        );
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 3,
        bgcolor: "background.default",
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Authentication Failed</Typography>
          <Typography>{error}</Typography>
        </Alert>
      ) : (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" align="center" gutterBottom>
            Social Authentication
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary">
            {status}
          </Typography>
          <Typography variant="caption" align="center" sx={{ mt: 2 }}>
            This window will close automatically...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default SocialAuthPopup;
